import { C, personalities, problems as problemList, type PersonalityId, type ProblemId, type TesterPool } from "@bd/content";
import type { Compiled } from "./design";
import type { Rng } from "./rng";
import type { Design, PlaytestResult, ProblemSighting, TableEvent, TesterReport } from "./state";
import { clamp } from "./util";

export const TESTER_NAMES = [
  "Rosa", "Big Lou", "Pip", "Mags", "Tobias", "Nell", "Arjun", "Bettina", "Cosmo", "Dee", "Elio", "Fern", "Gordy", "Hettie",
  "Ilse", "Jasper", "Kiki", "Lenny", "Moe", "Noor", "Otto", "Peg", "Ravi", "Suki", "Tilly", "Vern", "Wally", "Yusuf",
];

export interface PlaytestInput {
  design: Design;
  compiled: Compiled;
  problems: Record<ProblemId, number>; // true severities after fixes
  trueReplay: number;
  pool: TesterPool;
  detectBonus: number; // playtest lead, lab, traits: 0..0.4
  week: number;
  games?: number; // games played in this round (default 3)
  hint?: { slider: string; gap: number }; // the slider furthest from the ideal (see review.focusHint)
}

const FOCUS_QUOTES: Record<string, [string, string]> = {
  depth: ["I want harder choices.", "Too much to think about."],
  access: ["Took ages to learn.", "A bit too simple for me."],
  immersion: ["The theme feels pasted on.", "Less story, more game!"],
  interaction: ["I barely noticed the others.", "Stop attacking me!"],
  replay: ["I'd play it once.", "Too many options in the box."],
  art: ["It looks a bit plain.", "Pretty, but busy."],
  components: ["The pieces feel cheap.", "So many bits."],
  speed: ["Can we go faster?", "Over too quickly."],
};

interface Tester {
  name: string;
  p: PersonalityId;
  skill: number;
  score: number;
  fun: number[];
}

const TURNS = 10;

/** How much a personality enjoys the design's features, -0.3..0.3. */
function taste(p: PersonalityId, c: Compiled, d: Design): number {
  const likes = C.personality[p].likes;
  const feat: Record<string, number> = {
    decisions: clamp((c.decisions - 2.5) / 3, -1, 1),
    luck: clamp((c.luck - 0.4) / 0.4, -1, 1),
    depth: clamp((d.focus.depth - 2) / 3, -1, 1),
    interaction: clamp((c.interaction - 0.4) / 0.4, -1, 1),
    speed: clamp((d.focus.speed - 1) / 3, -1, 1),
    access: clamp((d.focus.access - 2) / 3, -1, 1),
    complexity: clamp((c.complexity - 0.3) / 0.4, -1, 1),
    clarity: clamp((c.clarity - 0.3) / 0.4, -1, 1),
  };
  let s = 0;
  for (const [k, w] of Object.entries(likes)) s += (feat[k] ?? 0) * w;
  return clamp(s * 0.12, -0.3, 0.3);
}

function line(p: PersonalityId, mood: string, r: Rng): string | undefined {
  const lines = C.personality[p].lines[mood];
  return lines?.length ? r.pick(lines) : undefined;
}

function pickTesters(pool: TesterPool, players: [number, number], r: Rng): Tester[] {
  const n = clamp(r.int(pool.testers[0], pool.testers[1]), Math.max(2, players[0]), Math.max(2, Math.min(6, players[1])));
  const names = r.shuffle([...TESTER_NAMES]).slice(0, n);
  const kinds = r.shuffle(personalities.map((x) => x.id));
  return names.map((name, i) => ({ name, p: kinds[i % kinds.length], skill: r.float(0.3, 0.9), score: 0, fun: [] }));
}

/** Which personalities make a problem easy to see. */
const SEES: Record<ProblemId, PersonalityId[]> = Object.fromEntries(problemList.map((p) => [p.id, p.sees as PersonalityId[]])) as any;

export function runPlaytest(input: PlaytestInput, r: Rng): PlaytestResult {
  const { design: d, compiled: c, problems: sev, pool } = input;
  const testers = pickTesters(pool, d.players, r);
  const games = input.games ?? 3;
  const events: TableEvent[] = [];
  const curve = new Array(TURNS).fill(0);
  const seen = new Map<ProblemId, ProblemSighting>();
  const present = new Set(testers.map((t) => t.p));

  for (let g = 0; g < games; g++) {
    for (const t of testers) {
      t.score = 0;
      t.fun = [];
    }
    const brokenTurn = sev.broken_card > 0.1 ? r.int(3, 7) : -1;
    const brokenHolder = r.int(0, testers.length - 1);
    for (let turn = 0; turn < TURNS; turn++) {
      // scores: skill vs luck, runaway leaders and catch-up rules
      const lead = Math.max(...testers.map((t) => t.score));
      const low = Math.min(...testers.map((t) => t.score));
      for (let i = 0; i < testers.length; i++) {
        const t = testers[i];
        const roll = r.next();
        let gain = (1 - c.luck) * t.skill + c.luck * roll;
        if (t.score === lead && lead > low) gain *= 1 + (1 - c.catchUp) * 0.6;
        if (t.score === low && lead > low) gain += c.catchUp * 0.3;
        if (turn === brokenTurn && i === brokenHolder) gain += 2.5 * sev.broken_card;
        t.score += gain;
        if (g === 0 && c.luck > 0.5 && roll > 0.9) events.push({ t: (turn + r.next()) / TURNS, kind: "roll", tester: i, line: line(t.p, "happy", r) });
      }
      const ranked = [...testers].sort((a, b) => b.score - a.score);
      const spread = ranked[0].score - ranked[ranked.length - 1].score;
      const progress = turn / (TURNS - 1);
      for (let i = 0; i < testers.length; i++) {
        const t = testers[i];
        let fun = 0.62 + taste(t.p, c, d) + r.normal(0, 0.03);
        const rank = ranked.indexOf(t);
        // downtime: everyone, worst for kids and social butterflies
        if (sev.downtime > 0) fun -= sev.downtime * (t.p === "kid" || t.p === "social" ? 0.32 : 0.2);
        // analysis paralysis: the analyst freezes in the midgame, the rest wait
        if (sev.ap > 0 && progress > 0.2 && progress < 0.8) fun -= sev.ap * (t.p === "analyst" ? 0.05 : 0.22);
        // too random: sharks and analysts hate it
        if (sev.too_random > 0) fun -= sev.too_random * (t.p === "shark" ? 0.38 : t.p === "analyst" ? 0.28 : t.p === "kid" ? -0.05 : 0.06);
        // runaway leader: losers go quiet from the midgame on
        if (sev.runaway > 0 && progress > 0.35 && rank > 0) fun -= sev.runaway * clamp(spread / 3) * 0.45;
        if (sev.runaway > 0 && rank === 0) fun += 0.05;
        // kingmaker: at the end a losing tester picks the winner
        if (sev.kingmaker > 0 && progress > 0.75) fun -= sev.kingmaker * (rank === 0 || rank === 1 ? 0.28 : 0.1);
        // solved strategy: visible from the second game
        if (sev.solved > 0) fun -= sev.solved * 0.12 * g;
        // broken card
        if (brokenTurn >= 0 && turn >= brokenTurn) fun -= sev.broken_card * (i === brokenHolder ? -0.08 : 0.25);
        // rules confusion: early turns
        if (sev.rules_confusion > 0 && progress < 0.4) fun -= sev.rules_confusion * (t.p === "kid" ? 0.45 : t.p === "rules_lawyer" ? 0.3 : 0.18);
        // drags: the fun curve sags at the end
        if (sev.drags > 0 && progress > 0.65) fun -= sev.drags * 0.4 * (progress - 0.6) / 0.4;
        // fiddly setup
        if (sev.fiddly > 0 && turn === 0) fun -= sev.fiddly * 0.4;
        // a closing swell when the game is good
        if (progress > 0.7) fun += 0.08 * (input.trueReplay - 0.5);
        t.fun.push(clamp(fun));
        curve[turn] += clamp(fun) / (testers.length * games);
      }
      if (g === 0) tableMoments(testers, turn, sev, c, ranked, r, events);
    }
    // detection: each true problem can be spotted in each game
    for (const p of problemList) {
      const s = sev[p.id];
      if (s < 0.06) continue;
      const vis = SEES[p.id].some((x) => present.has(x)) ? 1.25 : 0.8;
      const chance = clamp(clamp(s * 1.6) * (pool.accuracy + input.detectBonus) * vis * (g === 0 ? 0.85 : 1));
      if (r.chance(chance)) {
        const prev = seen.get(p.id);
        const observed = clamp(s + r.normal(0, 0.12 * (1.1 - pool.accuracy)));
        const conf: ProblemSighting["confidence"] = observed >= 0.25 || (prev && prev.confidence === "found") ? "found" : "watching";
        const turn = problemTurn(p.id, r);
        if (!prev || (prev.confidence === "watching" && conf === "found")) seen.set(p.id, { problem: p.id, confidence: conf, turn, severity: observed });
      }
    }
  }

  // friends and family are too kind; strangers are blunt
  const replayIntent = clamp(input.trueReplay + pool.kindness + r.normal(0, 0.1 * (1.05 - pool.accuracy)), 0, 1);
  const reports: TesterReport[] = testers.map((t) => {
    const fun = t.fun.reduce((s, v) => s + v, 0) / t.fun.length;
    const ranked = [...testers].sort((a, b) => b.score - a.score);
    let mood: TesterReport["mood"] = fun > 0.68 ? "hooked" : fun > 0.55 ? "happy" : fun > 0.42 ? "bored" : "frustrated";
    if (ranked[0] === t && fun > 0.5) mood = "winning";
    if (t.p === "rules_lawyer" && sev.rules_confusion > 0.25) mood = "arguing";
    if (t.p === "kid" && sev.rules_confusion > 0.35) mood = "confused";
    return { name: t.name, personality: t.p, mood, fun: clamp(fun) };
  });
  events.sort((a, b) => a.t - b.t);
  // testers sometimes say what the design is missing (the hidden ideal shape), more often when they're good
  let focusNote: PlaytestResult["focusNote"];
  const hint = input.hint;
  if (hint && Math.abs(hint.gap) >= 0.75 && r.chance(0.35 + 0.6 * pool.accuracy)) {
    const more = hint.gap > 0;
    focusNote = { slider: hint.slider, more, quote: FOCUS_QUOTES[hint.slider]?.[more ? 0 : 1] ?? (more ? `More ${hint.slider}!` : `Less ${hint.slider}.`) };
  }
  return {
    week: input.week,
    pool: pool.id,
    testers: reports,
    funCurve: curve.map((v) => Math.round(v * 1000) / 1000),
    replayIntent: Math.round(replayIntent * 100) / 100,
    problems: [...seen.values()].sort((a, b) => b.severity - a.severity),
    events: events.slice(0, 60),
    turns: TURNS,
    focusNote,
  };
}

function problemTurn(p: ProblemId, r: Rng): number {
  switch (p) {
    case "fiddly": return 1;
    case "rules_confusion": return r.int(1, 3);
    case "drags": case "kingmaker": return r.int(8, 10);
    case "runaway": return r.int(5, 7);
    default: return r.int(3, 8);
  }
}

/** Little moments for the animated table, taken from what the sim actually did. */
function tableMoments(testers: Tester[], turn: number, sev: Record<ProblemId, number>, c: Compiled, ranked: Tester[], r: Rng, out: TableEvent[]) {
  const t = turn / TURNS;
  const pickI = (p?: PersonalityId) => {
    const idx = testers.findIndex((x) => x.p === p);
    return idx >= 0 ? idx : r.int(0, testers.length - 1);
  };
  if (turn === 0 && sev.fiddly > 0.25) out.push({ t: 0.01, kind: "argue", tester: pickI("casual"), line: line("casual", "fiddly", r) ?? "So many bits..." });
  if (turn < 3 && sev.rules_confusion > 0.25 && r.chance(0.6)) out.push({ t: t + 0.03, kind: "argue", tester: pickI("rules_lawyer"), line: line("rules_lawyer", "confused", r) });
  if (sev.downtime > 0.2 && r.chance(0.5)) out.push({ t: t + 0.05, kind: "phone", tester: pickI("kid"), line: line("kid", "downtime", r) ?? line("social", "downtime", r) });
  if (sev.ap > 0.25 && turn > 2 && turn < 8 && r.chance(0.5)) out.push({ t: t + 0.04, kind: "think", tester: pickI("analyst"), line: line("analyst", "ap", r) });
  if (sev.too_random > 0.3 && r.chance(0.45)) out.push({ t: t + 0.06, kind: "slam", tester: pickI("shark"), line: line("shark", "random", r) });
  if (sev.runaway > 0.25 && turn >= 5 && r.chance(0.5)) {
    const loser = testers.indexOf(ranked[ranked.length - 1]);
    out.push({ t: t + 0.07, kind: "lean_back", tester: loser, line: "I can't catch up." });
  }
  if (sev.drags > 0.25 && turn >= 8 && r.chance(0.6)) out.push({ t: t + 0.05, kind: "lean_back", tester: pickI("casual"), line: line("casual", "drags", r) });
  if (sev.kingmaker > 0.25 && turn === 9) out.push({ t: 0.95, kind: "argue", tester: pickI("social"), line: line("social", "kingmaker", r) });
  if (sev.broken_card > 0.3 && turn === 6) out.push({ t: 0.62, kind: "slam", tester: pickI("shark"), line: line("shark", "broken", r) });
  if (c.interaction > 0.55 && r.chance(0.35)) out.push({ t: t + 0.02, kind: "laugh", tester: pickI("social"), line: line("social", "happy", r) });
  if (r.chance(0.5)) out.push({ t: t + 0.01, kind: c.luck > 0.45 ? "roll" : "flip", tester: turn % testers.length });
  if (turn === 9) {
    const w = testers.indexOf(ranked[0]);
    out.push({ t: 0.98, kind: "win", tester: w, line: line(ranked[0].p, "happy", r) ?? "One more round!" });
  }
}

/** Mood of the session as one number, for bots and summaries. */
export function averageFun(res: PlaytestResult): number {
  return res.funCurve.reduce((s, v) => s + v, 0) / res.funCurve.length;
}
